import {
  Badge,
  Checkbox,
  FlexBox,
  Icon,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { SearchConversationEnriched } from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ConversationStatus } from 'lib-shared-types-api';

import { AppLink } from 'components/appLink';
import { ConversationChannelIcon } from 'components/conversation/conversationChannelIcon';
import { getChannelName } from 'utils/channels';

import styles from './conversationTimelineDetailsItem.module.scss';

type ConversationTimelineDetailsItemProps = {
  conversation: SearchConversationEnriched;
  title: string | undefined;
  linkedTo: number | undefined;
  isCurrentConversation: boolean;
  isChecked: boolean;
  status: ConversationStatus | undefined;
  onCheck: (id: number) => void;
};

export const ConversationTimelineDetailsItem = ({
  conversation,
  title,
  linkedTo,
  isChecked,
  status,
  isCurrentConversation,
  onCheck,
}: ConversationTimelineDetailsItemProps): JSX.Element => {
  const { id, created_at, initial_channel, queue, linked_to } = conversation;
  const csidLink =
    linkedTo !== -1
      ? (linked_to?.split('_').at(0) ?? linkedTo?.toString())
      : undefined;
  const conversationStatus = status ?? conversation.status;

  const rootClass = classnames({
    [styles.root]: true,
    [styles.ownConversation]: isCurrentConversation,
  });

  const badgeClass = classnames({
    [styles.badge]: true,
    [styles.badgeOpen]: conversationStatus === 'open',
    [styles.badgePending]:
      conversationStatus === 'pending' ||
      conversationStatus === 'awaitingpending',
  });

  return (
    <FlexBox className={rootClass}>
      <FlexBox className={styles.checkbox}>
        <Checkbox
          id={`${id}`}
          checked={isCurrentConversation || isChecked}
          disabled={isCurrentConversation || !!csidLink}
          onChange={() => onCheck(id)}
        ></Checkbox>
      </FlexBox>
      <AppLink
        className={styles.content}
        path="/conversation/:csid"
        params={{ path: { csid: `${id}` } }}
      >
        <FlexBox className={styles.statusBar}>
          <Badge className={badgeClass} variant="secondary" size="medium">
            <Tooltip
              content={initial_channel ? getChannelName(initial_channel) : ''}
              wrap="none"
            >
              <ConversationChannelIcon
                color="neutral.000"
                channel={
                  convertChannelCasing(
                    initial_channel,
                    'truncate',
                  ) as ConversationsChannels
                }
              />
            </Tooltip>
            <Text.Paragraph as="span" className={styles.csid} variant="3">
              #{id}
            </Text.Paragraph>
          </Badge>
          {csidLink && (
            <Tooltip content={`Linked to #${csidLink}`}>
              <FlexBox className={styles.linkIcon}>
                <Icon name="link" />
              </FlexBox>
            </Tooltip>
          )}
        </FlexBox>
        <Text.Paragraph subdued as="span" variant="1" clamp={2} padding="100 0">
          {title}
        </Text.Paragraph>
        <FlexBox className={styles.contentDetails}>
          {queue && (
            <Text.Paragraph
              subdued
              as="span"
              variant="3"
              maxWidth="50%"
              wrap="nowrap"
            >
              <Tooltip content={queue.name}>{queue.name}</Tooltip>
            </Text.Paragraph>
          )}
          {created_at && (
            <Timestamp
              time={created_at}
              relative
              onClick={(_, e) => e.preventDefault()}
            />
          )}
        </FlexBox>
      </AppLink>
    </FlexBox>
  );
};
